import React from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { useTranslation } from "@/lib/i18n";
import { useAgentsStore, useSubscribeToAgent, Agent } from "@/lib/agents-store";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Star, Zap, Globe, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
export default function MarketplacePage() {
  const { t } = useTranslation();
  const agents = useAgentsStore((s) => s.agents);
  const subscribeToAgent = useSubscribeToAgent();
  const marketplaceAgents = agents.filter(a => a.status === 'marketplace');
  const handleSubscribe = (agent: Agent) => {
    subscribeToAgent(agent);
    toast.success(`Subscribed to ${agent.displayName}!`);
  };
  return (
    <AppLayout container>
      <div className="space-y-12">
        {/* Hero Section */}
        <section className="relative overflow-hidden rounded-3xl bg-sidebar px-8 py-16 text-sidebar-foreground">
          <div className="absolute inset-0 bg-gradient-mesh opacity-25" />
          <div className="relative z-10 max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <Badge className="border-primary/30 bg-primary/20 text-primary-foreground">
                Agent Marketplace
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
                Supercharge Your Workforce with Specialized AI
              </h1>
              <p className="text-lg text-sidebar-foreground/72">
                Discover pre-trained agents developed by the community and enterprise partners.
              </p>
              <div className="flex gap-4">
                <Button className="bg-gradient-primary text-primary-foreground hover:opacity-95">Explore Top Picks</Button>
                <Button variant="outline" className="border-white/20 text-sidebar-foreground hover:bg-white/10">Publish Agent</Button>
              </div>
            </motion.div>
          </div>
        </section>
        {/* Categories */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {['Productivity', 'Marketing', 'Development', 'Sales', 'Legal', 'Support'].map((cat) => (
            <Button key={cat} variant="outline" className="h-12 border-white/10 bg-card/40 backdrop-blur-sm">
              {cat}
            </Button>
          ))}
        </div>
        {/* Marketplace Grid */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-amber-500" />
              Featured Agents
            </h2>
            <Button variant="link">View All</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {marketplaceAgents.map((agent, i) => (
              <motion.div
                key={agent.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="overflow-hidden border-white/10 bg-card/40 backdrop-blur-sm group">
                  <div className="aspect-video relative overflow-hidden">
                    <img src={agent.avatarUrl} alt={agent.name} className="object-cover w-full h-full" />
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-black/60 backdrop-blur-md border-0">
                        ${agent.price}/mo
                      </Badge>
                    </div>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <Badge variant="secondary" className="text-[10px]">{agent.role}</Badge>
                      <div className="flex items-center text-amber-500 text-xs font-bold gap-1">
                        <Star className="w-3 h-3 fill-current" /> 4.9
                      </div>
                    </div>
                    <h3 className="text-lg font-bold truncate">{agent.displayName}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 h-10">
                      {agent.description}
                    </p>
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/5">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Zap className="w-3 h-3" /> {agent.metrics.successRate}% Success
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Globe className="w-3 h-3" /> {agent.languages.join(', ')}
                      </div>
                    </div>
                    <Button
                      className="w-full gap-2 bg-gradient-primary text-primary-foreground hover:opacity-95"
                      onClick={() => handleSubscribe(agent)}
                    >
                      <ShoppingCart className="w-4 h-4" /> Subscribe
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
